// Items: descriptions and position-weighted draw (riders at the back get the powerful items).
export type ItemId = 'turbo' | 'banana' | 'shell' | 'shield' | 'magnet' | 'jam' | 'storm' | 'fog' | 'bomb' | 'crash';
export interface ItemDef { icon: string; name: string; help: string }

export const ITEMS: Record<ItemId, ItemDef> = {
  turbo: { icon: '⚡', name: 'Turbo', help: '+50% power for 4 s, the trainer gets lighter' },
  banana: { icon: '🍌', name: 'Banana', help: 'Dropped behind you. Whoever rides over it slips and gets heavy legs' },
  shell: { icon: '🎯', name: 'Missile', help: 'Homes in on the rider just ahead and hits anything in its way' },
  shield: { icon: '🛡️', name: 'Shield', help: 'Blocks the next attack for 10 s' },
  magnet: { icon: '🧲', name: 'Draft magnet', help: 'Maximum draft for 8 s, even riding alone' },
  jam: { icon: '🔒', name: 'Jammed derailleur', help: 'Locks the gears of the rider ahead for 6 s' },
  storm: { icon: '⛈️', name: 'Headwind', help: 'A storm blows on the leader for 5 s' },
  fog: { icon: '🌫️', name: 'Fog', help: 'Blinds and slows every rider ahead of you for 4 s' },
  bomb: { icon: '💣', name: 'Watt bomb', help: 'Sprint for 5 s: the more your average beats your FTP, the bigger the boost' },
  crash: { icon: '💥', name: 'Crash', help: 'Brings down the rider just ahead. Whoever rides into the fallen rider goes down too' },
};

/** Field size up to which the item odds are those of a small race; a fog in a bigger pack blinds proportionally fewer riders. */
export const SMALL_FIELD = 12;

/**
 * Draw weights: p = 0 for the leader, 1 for the last rider. `field`: riders still racing. The fog hits every rider ahead,
 * so in a 50-rider pack one fog from the back blinds 49 riders: its odds shrink with the field so the blinded
 * rider-seconds per minute stay those of a small race (measured: 0.4 s at 8 riders, 1.6 s at 50 without this).
 */
export function itemWeights(p: number, arena: boolean, field = SMALL_FIELD): Record<ItemId, number> {
  const crowd = Math.min(1, SMALL_FIELD / Math.max(1, field));
  return {
    banana: 30 * (1 - p) + 6 + (arena ? 15 : 0),
    shield: 18 * (1 - p) + 5,
    shell: 12 + 12 * p + (arena ? 20 : 0),
    turbo: arena ? 6 : 4 + 30 * p,
    magnet: arena ? 0 : 4 + 18 * p,
    bomb: arena ? 3 : 4 + 14 * p,
    jam: 7 + 6 * p,
    storm: arena ? 6 : p > 0.35 ? 26 * p : 0,
    fog: (6 + 12 * p) * crowd,
    // a crash can bring down a whole bunch: as rare in a big pack as the fog
    crash: (3 + 9 * p) * crowd,
  };
}

/** Item held by a rider: official, or created ("c:<id>"). */
export type ItemKey = ItemId | `c:${string}`;
export const customKey = (id: string): ItemKey => `c:${id}`;

export interface RollOptions {
  arena?: boolean; pool?: ItemId[]; boost?: Partial<Record<ItemId, number>>;
  /** Created items: weight interpolated between the front and the back of the field. */
  custom?: { id: string; content: { odds: { front: number; back: number } } }[];
  /** False: only created items come out of the boxes. */
  builtin?: boolean;
  /** Riders still racing (see itemWeights). */
  field?: number;
}

/** Draws an item: weights by position, filtered by the allowed items and the mode's multipliers. */
export function rollItem(p: number, o: RollOptions, rnd: () => number): ItemKey {
  const w = itemWeights(p, !!o.arena, o.field);
  const keys: ItemKey[] = o.builtin === false ? [] : (o.pool?.length ? o.pool : (Object.keys(w) as ItemId[])).filter(k => k in w);
  const ws = keys.map(k => Math.max(0, w[k as ItemId] * (o.boost?.[k as ItemId] ?? 1)));
  for (const c of o.custom || []) { keys.push(customKey(c.id)); ws.push(Math.max(0, c.content.odds.front * (1 - p) + c.content.odds.back * p)); }
  const tot = ws.reduce((a, b) => a + b, 0);
  if (tot <= 0) return keys[0] || 'banana';
  let x = rnd() * tot;
  for (let i = 0; i < keys.length; i++) { x -= ws[i]; if (x <= 0) return keys[i]; }
  return keys[keys.length - 1];
}

export const ITEM_IDS = Object.keys(ITEMS) as ItemId[];

/** Temporary effects: label and nature for the UI. */
export type EffectId = 'turbo' | 'bomb' | 'charge' | 'magnet' | 'heavy' | 'spin' | 'jam' | 'headwind' | 'fog' | 'cheer' | 'crash';
export const EFFECTS: Record<EffectId, [string, 'good' | 'bad']> = {
  turbo: ['⚡ Turbo', 'good'], bomb: ['💥 Boost', 'good'], charge: ['💣 Sprint!', 'good'], magnet: ['🧲 Draft', 'good'], cheer: ['📣 Crowd', 'good'],
  heavy: ['🦵 Heavy legs', 'bad'], spin: ['💫 Dazed', 'bad'], jam: ['🔒 Gears locked', 'bad'], headwind: ['⛈️ Headwind', 'bad'], fog: ['🌫️ Fog', 'bad'], crash: ['🤕 Crashed', 'bad'],
};
